import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import * as Lucide from "lucide-react";
import { ArrowLeft, ArrowRight, Sparkles, Loader2, ShieldCheck, Plus, FolderPlus } from "lucide-react";

/**
 * ELIA · Module detail page — presents one ELIA module, its underlying
 * ALTIORA technical engines, and (when applicable) the archetypes of
 * child dossiers you can instantiate from within.
 */
export default function EliaModulePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [engines, setEngines] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/elia-modules/${slug}`);
        setModule(data);
        const [{ data: allModules }, { data: myDoss }] = await Promise.all([
          api.get("/modules/"),
          api.get("/dossiers/").catch(() => ({ data: { items: [] } })),
        ]);
        const map = new Map((allModules.items || []).map((m) => [m.id, m]));
        setEngines((data.altiora_modules || []).map((id) => map.get(id)).filter(Boolean));
        // Filter dossiers already spawned from this ELIA module (parents only)
        setDossiers((myDoss.items || []).filter((d) => d.elia_module === data.slug));
      } catch (err) {
        toast.error("Module ALTIORA introuvable");
        navigate("/app");
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const openMyEspace = async () => {
    if (!module) return;
    // If the user has no dossier for this ELIA module yet, create-flow with elia hint
    if (dossiers.length === 0) {
      navigate(`/app/dossiers/new?elia=${module.slug}&subtype=self`);
    } else {
      // Otherwise open the most recent one
      navigate(`/app/dossiers/${dossiers[0].dossier_id}`);
    }
  };

  const openChildFlow = (childType) => {
    if (!module) return;
    const params = new URLSearchParams({
      elia: module.slug,
      subtype: childType.key,
      client_type: childType.default_client_type,
    });
    // If a parent dossier exists for this ELIA hub, attach the child to it
    if (dossiers.length > 0) params.set("parent_id", dossiers[0].dossier_id);
    navigate(`/app/dossiers/new?${params.toString()}`);
  };

  if (loading || !module) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F7F3]">
        <Loader2 className="h-4 w-4 animate-spin text-[#0B2545]" strokeWidth={1.5} />
      </div>
    );
  }

  const Icon = Lucide[toPascal(module.icon)] || Sparkles;
  const hasChildren = (module.child_dossier_types || []).length > 0;

  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937] relative overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[500px]">
        <div
          className="absolute top-[-15%] right-[-8%] w-[600px] h-[600px] rounded-full blur-3xl opacity-15"
          style={{ backgroundColor: module.accent_color }}
        />
      </div>

      <main className="relative max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="elia-module-page">
        <Link to="/app" className="text-xs font-mono uppercase tracking-[0.24em] text-[#5B5B62] hover:text-[#0B2545]" data-testid="elia-module-back">
          <ArrowLeft className="inline h-3.5 w-3.5 mr-1 -mt-0.5" strokeWidth={2} /> Écosystème ALTIORA
        </Link>

        <div className="mt-10 flex items-start gap-6">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="h-16 w-16 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${module.accent_color}18`, color: module.accent_color }}
          >
            <Icon className="h-8 w-8" strokeWidth={1.5} />
          </motion.div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
              Module ALTIORA · Pour {module.audience_fr.toLowerCase()}
            </div>
            <h1 className="mt-2 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1F2937]">
              {module.name_fr}
            </h1>
            <p className="mt-4 text-lg text-[#7A5B14] italic leading-relaxed">« {module.tagline_fr} »</p>
          </div>
        </div>

        <p className="mt-10 text-[15px] md:text-base leading-relaxed text-[#5B5B62] max-w-2xl">
          {module.description_fr}
        </p>

        {/* Primary CTA — open my own space */}
        <div className="mt-10">
          <button
            onClick={openMyEspace}
            disabled={module.coming_soon}
            data-testid="elia-module-start"
            className="group inline-flex items-center gap-3 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3.5 text-sm font-medium transition-all"
          >
            {module.coming_soon
              ? "Bientôt disponible"
              : dossiers.length > 0
                ? "Ouvrir mon espace"
                : "Ouvrir mon espace"}
            {!module.coming_soon && <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />}
          </button>
          <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-[#8B8B8F]">
            Un dossier ALTIORA sera créé pour vous. Vous restez propriétaire de vos données.
          </div>
        </div>

        {/* Child dossier types — for modules like Family/Business/Institution */}
        {hasChildren && !module.coming_soon && (
          <section className="mt-14" data-testid="elia-module-children">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
              Dossiers spécifiques
            </div>
            <h2 className="mt-2 font-display text-2xl tracking-tight text-[#1F2937]">
              Créez des dossiers pour vos proches ou vos structures
            </h2>
            <p className="mt-2 text-sm text-[#5B5B62] max-w-2xl leading-relaxed">
              Chaque dossier reste rattaché à votre espace {module.name_fr} et hérite de ses
              moteurs ALTIORA. Vous pouvez en ouvrir autant que nécessaire.
            </p>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              {(module.child_dossier_types || []).map((c) => {
                const CIco = Lucide[toPascal(c.icon)] || FolderPlus;
                return (
                  <button
                    key={c.key}
                    onClick={() => openChildFlow(c)}
                    data-testid={`elia-module-create-child-${c.key}`}
                    className="text-left rounded-xl border border-[#E4E1D8] bg-white hover:border-[#0B2545] hover:shadow-md p-5 transition-all group"
                    style={{ borderLeft: `4px solid ${c.accent_color}` }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div
                        className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${c.accent_color}15`, color: c.accent_color }}
                      >
                        <CIco className="h-5 w-5" strokeWidth={1.5} />
                      </div>
                      <Plus className="h-4 w-4 text-[#B8B5AC] group-hover:text-[#0B2545] transition-colors" strokeWidth={2} />
                    </div>
                    <div className="mt-4 font-display text-lg tracking-tight text-[#1F2937]">
                      {c.label_fr}
                    </div>
                    <p className="mt-1.5 text-sm text-[#5B5B62] leading-relaxed">{c.description_fr}</p>
                    <div className="mt-3 inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#0B2545] group-hover:translate-x-0.5 transition-transform">
                      Créer un dossier <ArrowRight className="h-3 w-3" strokeWidth={2} />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Existing dossiers spawned from this ELIA hub */}
        {dossiers.length > 0 && (
          <section className="mt-14" data-testid="elia-module-existing-dossiers">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
              Vos dossiers rattachés à {module.name_fr}
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {dossiers.map((d) => (
                <Link
                  key={d.dossier_id}
                  to={`/app/dossiers/${d.dossier_id}`}
                  data-testid={`elia-module-open-dossier-${d.dossier_id}`}
                  className="rounded-xl border border-[#E4E1D8] bg-white hover:border-[#0B2545] p-4 transition-all group"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-display text-sm text-[#1F2937] tracking-tight truncate">{d.name}</div>
                    <ArrowRight className="h-4 w-4 text-[#B8B5AC] group-hover:text-[#0B2545] group-hover:translate-x-0.5 transition-all" strokeWidth={1.5} />
                  </div>
                  <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                    {d.type === "individual" ? "Personne" : "Organisation"} · {d.subtype || "principal"}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Underlying engines */}
        <section className="mt-16" data-testid="elia-module-engines">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
            Alimenté par le moteur ALTIORA
          </div>
          <h2 className="mt-2 font-display text-2xl tracking-tight text-[#1F2937]">
            Les {engines.length} moteur{engines.length > 1 ? "s" : ""} qui rendent {module.name_fr} possible
          </h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            {engines.map((e) => (
              <div
                key={e.id}
                className="rounded-xl border border-[#E4E1D8] bg-white p-5 shadow-sm"
                style={{ borderLeft: `4px solid ${e.color_accent}` }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[9px] font-mono uppercase tracking-widest text-[#5B5B62]">
                      {e.category} · {e.subscription_tier}
                    </div>
                    <div className="mt-1 font-display text-lg tracking-tight text-[#1F2937]">
                      {e.name_fr}
                    </div>
                  </div>
                  <Plus className="h-4 w-4 text-[#B8B5AC]" strokeWidth={2} />
                </div>
                <p className="mt-2 text-sm text-[#5B5B62] leading-relaxed line-clamp-3">{e.description_fr}</p>
                {e.required_licenses?.length > 0 && (
                  <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] bg-[#FBF6EA] rounded-full px-2 py-0.5">
                    <ShieldCheck className="h-2.5 w-2.5" strokeWidth={2} /> Habilitation pro requise
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <div className="mt-16 rounded-lg border border-[#E4E1D8] bg-white/60 px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
          ALTIORA · navigateur économique intelligent — méthode v3
        </div>
      </main>
    </div>
  );
}

function toPascal(name) {
  if (!name) return "Circle";
  return name.split("-").map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join("");
}
